/**
 * MaterialStatusPill — renders the task-level 5-state material readiness
 * pill (FEAT-TASKS-MATERIALS-STATUS-COLUMN). Reuses the design-system
 * StatusPill primitive per R4.5 (no fifth pill primitive); maps:
 *
 *   null     → "—" muted gray, no pill
 *   'none'     → StatusPill not_started + "Not needed"
 *   'required' → StatusPill blocked     + "Needs materials"
 *   'sourcing' → StatusPill in_progress + "Sourcing"
 *   'ready'    → StatusPill completed   + "Ready"
 *
 * Because StatusPill's intrinsic label is the underlying schedule status
 * ("Not started", "Blocked", etc.) we render the swatch + dot ourselves
 * using the design-system status-tone CSS vars and apply the materials-
 * specific copy on top.
 */
import { StatusValue } from '../../design-system/primitives/primitives'
import { MaterialStatus, MATERIAL_STATUS_LABEL } from '../../lib/scheduleTypes'

const STATUS_TONE: Record<MaterialStatus, StatusValue> = {
  none:     'not_started',
  required: 'blocked',
  sourcing: 'in_progress',
  ready:    'completed',
}

export interface MaterialStatusPillProps {
  status: MaterialStatus | null | undefined
  size?: 'sm' | 'md'
  style?: React.CSSProperties
}

export function MaterialStatusPill({ status, size = 'sm', style = {} }: MaterialStatusPillProps) {
  if (!status) {
    return (
      <span style={{
        color: 'var(--text-dim)',
        fontSize: 12.5,
        fontFamily: 'var(--font-sans)',
        ...style,
      }}>—</span>
    )
  }
  const tone = STATUS_TONE[status]
  const key = tone.replace(/_/g, '-')
  const sizing = size === 'sm' ? { py: 3, px: 8, fs: 11 } : { py: 5, px: 10, fs: 12.5 }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: `${sizing.py}px ${sizing.px}px`, borderRadius: 'var(--r-sm)',
      background: `var(--status-${key}-bg)`,
      color:      `var(--status-${key}-tx)`,
      fontFamily: 'var(--font-sans)', fontSize: sizing.fs, fontWeight: 500,
      lineHeight: 1, letterSpacing: 0.1, whiteSpace: 'nowrap',
      ...style,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--status-${key}-dot)` }}/>
      {MATERIAL_STATUS_LABEL[status]}
    </span>
  )
}

export default MaterialStatusPill
